import { momentumText } from "../format";

/**
 * The facts a narrator may use: a compact, serialisable picture of the
 * ranked insights — names, numbers (rounded), the template wording, the
 * tenant's vocabulary. Nothing here is a React object or a tree node; the
 * BE hashes this to key its cache, so it must be deterministic for the
 * same insights.
 */

const round1 = (n) => Math.round(n * 10) / 10;
const round2 = (n) => Math.round(n * 100) / 100;

// Insight `numbers` can carry scored items (a topic's top row, a win's
// pair, a dataset's item). Flatten those to their label; round every
// number; drop anything that is not data.
const plain = (v, depth = 0) => {
  if (depth > 6) return undefined;
  if (typeof v === "number") return Number.isFinite(v) ? round1(v) : null;
  if (typeof v === "string" || typeof v === "boolean" || v === null) return v;
  if (Array.isArray(v)) return v.map((x) => plain(x, depth + 1)).filter((x) => x !== undefined);
  if (v && typeof v === "object") {
    if (v.nodeId && v.label) return v.label;
    const out = {};
    for (const [k, x] of Object.entries(v)) {
      if (k === "item" || k === "row" || k === "observations" || typeof x === "function") continue;
      const p = plain(x, depth + 1);
      if (p !== undefined) out[k] = p;
    }
    return out;
  }
  return undefined;
};

// Momentum in the words the narrator is allowed to use — a direction and a
// strength, plus the shares behind it — never the internal "swings" /
// index vocabulary, which the model would otherwise echo.
const momentumFacts = (s, meta) => {
  const m = s.momentum;
  if (!m?.available) return { direction: "unknown", note: momentumText(s, meta).text };
  const idx = m.index ?? 0;
  const strength = Math.abs(idx) >= 2 ? "much faster than usual" : Math.abs(idx) > 1 ? "faster than usual" : "about as usual";
  const direction = m.band === "new" ? "new this period" : m.band === "rising" ? `rising ${strength}` : m.band === "cooling" ? `cooling ${strength}` : "steady";
  const base = {
    direction,
    shareOfAllFeedbackNow: `${round1(m.shareNow)}%`,
  };
  if (m.mode === "history") return { ...base, usualShare: `${round1(m.baseline)}%`, readAgainst: `${m.history.length} earlier periods` };
  if (m.prev) return { ...base, previousShare: `${round1(m.prev.share)}%` };
  return base;
};

const subjectFacts = (s, meta, titleByContentType) => ({
  id: s.nodeId,
  label: s.label,
  topic: s.bucketLabel || null,
  signal: s.signal?.display || null,
  zone: s.zone || (s.isWin ? "win" : null),
  severity: round2(s.severity.value),
  momentum: momentumFacts(s, meta),
  observations: s.n,
  conversations: s.severity.docs,
  datasets: s.datasets.map((ds) => titleByContentType[ds] || ds),
});

export const buildNarrationFacts = ({ insights, meta, zones, copy, titleByContentType = {}, limit = 12 }) => ({
  period: {
    label: meta.nowLabel,
    grain: meta.timeGrain,
    totalObservations: meta.totalNow,
    comparedWith:
      meta.mode === "history"
        ? `each sub-topic's own normal over ${meta.historyPeriods} earlier ${meta.timeGrain}s`
        : meta.prevLabel
        ? `the previous period (${meta.prevLabel})`
        : null,
  },
  vocabulary: {
    zones: Object.fromEntries(Object.entries(zones).map(([k, z]) => [k, z.label])),
    nouns: copy.nouns,
  },
  insights: insights.slice(0, limit).map((i) => ({
    id: i.id,
    kind: i.kind,
    kindLabel: copy.kinds[i.kind] || i.kind,
    templateClaim: i.claim,
    templateWhy: i.why,
    topic: i.topic?.label || i.subject?.bucketLabel || null,
    subjects: i.subjects.map((s) => subjectFacts(s, meta, titleByContentType)),
    numbers: plain(i.numbers),
    rank: round2(i.rank),
  })),
});

// A cheap, stable string hash for the react-query key (the BE does the real
// content hash for its cache).
export const quickHash = (str) => {
  let h = 5381;
  for (let i = 0; i < str.length; i += 1) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};
